import React from 'react';
import { t } from '../utils/localization/localization';
import { useLanguage } from '../contexts/LanguageContext';
import { standardizeItems } from '../utils/versionManager';
import { logger } from '../utils/logger';
import { useLanguageAwareRender } from '../hooks/useLanguageAwareRender';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ReactMarkdown from 'react-markdown';
import AnalyzerPage from '../components/AnalyzerPage';

// Reusable steps for Company Research defined as a function to ensure translations update
const getSteps = () => [
  t('companyResearch.enterDetailsStep'),
  t('companyResearch.reviewResearchStep')
];

/**
 * Company Research Page - Refactored to use shared AnalyzerPage component
 */
function CompanyResearchPage({ userId, viewOnly = false, onComplete, onStatusChange }) {
  // Get language context to force re-renders on language change
  const { currentLanguage: _currentLanguage } = useLanguage();

  // Use standardized language-aware render hook
  const languageRenderKey = useLanguageAwareRender('CompanyResearchPage');

  // Get initial state for the Company Research analyzer
  const getInitialState = () => ({
    editingSectionIndex: null,
    editedContent: ''
  });

  // Process uploaded files (extract text if needed)
  const processFiles = (acceptedFiles, setText) => {
    // For now, just handle TXT files directly
    // In a real implementation, would extract text from various file formats
    const textFile = acceptedFiles.find(file => file.type === 'text/plain');
    if (textFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setText(e.target.result);
      };
      reader.readAsText(textFile);
    }
  };

  // Handle editing a section
  const handleEditSection = (section, index, updateCustomState) => {
    updateCustomState({
      editingSectionIndex: index,
      editedContent: section.content
    });
  };

  // Handle saving the edited section
  const handleSaveEdit = async (
    customState,
    updateCustomState,
    analysisResult,
    setAnalysisResult,
    setOpenEditDialog,
    _handleSaveCompanyData
  ) => {
    const { editingSectionIndex, editedContent } = customState;

    // Create a new array of sections
    const updatedSections = [...(analysisResult.sections || [])];
    const sectionName = updatedSections[editingSectionIndex]?.name || 'Unknown Section';

    // Update the content of the specified section
    updatedSections[editingSectionIndex] = {
      ...updatedSections[editingSectionIndex],
      content: editedContent
    };

    // Standardize the data to ensure all required fields
    const standardizedData = standardizeItems({
      ...analysisResult,
      sections: updatedSections
    }, 'company');

    try {
      // Update the analysis result first
      setAnalysisResult(standardizedData);

      // Save as new version using the function from AnalyzerPage
      if (_handleSaveCompanyData) {
        await _handleSaveCompanyData(sectionName);
        logger.info('[COMPANY PAGE]', 'Section edited and saved successfully:', sectionName);
      } else {
        logger.warn('[COMPANY PAGE]', 'No save function provided');
      }

      // Reset editing state
      updateCustomState({
        editingSectionIndex: null,
        editedContent: ''
      });

      // Close the dialog
      setOpenEditDialog(false);

    } catch (error) {
      logger.error('[COMPANY PAGE]', 'Error saving edited section:', error);
      // Error will be handled by AnalyzerPage and shown to user
    }
  };

  // Render the company research display
  const renderDataDisplay = (
    analysisResult,
    customState,
    updateCustomState,
    setOpenEditDialog,
    _setAnalysisResult,
    _handleDeleteSkill,
    _setActiveStep,
    _startEditing,
    _stopEditing,
    _handleSaveCompanyData
  ) => {
    logger.info('[COMPANY PAGE]', 'renderDataDisplay called with:', analysisResult);

    if (!analysisResult) {
      logger.warn('[COMPANY PAGE]', 'No analysis result provided');
      return null;
    }

    // Check data structure
    logger.info('[COMPANY PAGE]', 'Analysis result structure:', {
      hasCompanyName: !!analysisResult.companyName,
      companyName: analysisResult.companyName,
      hasSections: Array.isArray(analysisResult.sections),
      sectionsCount: Array.isArray(analysisResult.sections) ? analysisResult.sections.length : 0,
      hasReport: !!analysisResult.report,
      reportLength: analysisResult.report ? analysisResult.report.length : 0
    });

    // Extract data from result
    const sections = analysisResult.sections || [];
    const companyName = analysisResult.companyName || '';

    logger.info('[COMPANY PAGE]', 'Processing sections:', sections);

    // If no sections, but we have a report, create a default section
    if (sections.length === 0 && analysisResult.report) {
      logger.info('[COMPANY PAGE]', 'No sections but report exists, creating default section');
      sections.push({
        name: 'Company Overview',
        content: analysisResult.report
      });
    }

    // Check if we have content to display
    if (!companyName && sections.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            {t('companyResearch.noDataToDisplay')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ height: '100%', overflow: 'auto', p: 3 }}>
        {/* Company Name */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom align="center">
            {companyName || t('common.unknown')}
          </Typography>
        </Paper>

        {/* Research Sections */}
        {sections.map((section, index) => {
          // Get the status from the comparison data if available
          const status = section.status || 'unchanged';
          const isAdded = status === 'added';
          const isModified = status === 'modified';

          // Custom styling based on status
          const sectionStyle = {
            backgroundColor:
              isAdded ? 'rgba(76, 175, 80, 0.05)' :
                isModified ? 'rgba(33, 150, 243, 0.05)' :
                  'inherit',
            padding: '16px',
            borderRadius: '4px',
            marginBottom: '16px',
            position: 'relative'
          };

          return (
            <Paper key={`section-${index}`} elevation={2} sx={sectionStyle}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {section.name}
                </Typography>

                {!viewOnly && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      handleEditSection(section, index, updateCustomState);
                      setOpenEditDialog(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ mt: 2 }}>
                <ReactMarkdown>{section.content}</ReactMarkdown>
              </Box>
            </Paper>
          );
        })}
      </Box>
    );
  };

  // Render the edit dialog for company research sections
  const renderEditDialog = (
    open,
    setOpen,
    customState,
    updateCustomState,
    analysisResult,
    setAnalysisResult,
    _handleSaveCompanyData
  ) => {
    if (customState.editingSectionIndex === null) return null;

    const { editingSectionIndex, editedContent } = customState;
    const sections = analysisResult?.sections || [];
    const section = sections[editingSectionIndex];

    if (!section) return null;

    // Handle text change
    const handleTextChange = (e) => {
      updateCustomState({
        ...customState,
        editedContent: e.target.value
      });
    };

    return (
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('companyResearch.editSection', { name: section.name })}
        </DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={12}
            fullWidth
            variant="outlined"
            value={editedContent}
            onChange={handleTextChange}
            sx={{ mt: 1 }}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('companyResearch.preview')}:
            </Typography>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <ReactMarkdown>{editedContent}</ReactMarkdown>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={() => handleSaveEdit(
              customState,
              updateCustomState,
              analysisResult,
              setAnalysisResult,
              setOpen,
              _handleSaveCompanyData
            )}
            variant="contained"
            color="primary"
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Define the upload and text props for the first step
  // Using exact static strings instead of translation keys to avoid any potential caching issues
  const companyResearchProps = {
    uploadLabel: 'Upload company information',
    dropzoneText: 'Upload additional company documents (optional)',
    textLabel: t('companyResearch.companyName'),
    processFiles: processFiles,
    required: true,        // Make company name required
    fileUploadOptional: true  // Make file upload optional
  };

  // The main component using shared AnalyzerPage
  return (
    <AnalyzerPage
      key={languageRenderKey}
      type="company"
      title={t('companyResearch.title')}
      steps={getSteps()}
      userId={userId}
      viewOnly={viewOnly}
      onComplete={onComplete}
      onStatusChange={onStatusChange}
      renderDataDisplay={renderDataDisplay}
      renderEditDialog={renderEditDialog}
      getInitialState={getInitialState}
      processFiles={processFiles}
      uploadProps={companyResearchProps}
    />
  );
}

export default CompanyResearchPage;
